/**
 * Contact groups schemas — client-side validation for /api/contacts/groups/*.
 *
 * Mirrors LIMITS + DSL constraints from:
 *   - apps/api/src/lib/contacts-groups.ts  (GROUP_LIMITS)
 *   - apps/api/src/lib/contacts-filter.ts  (DSL whitelist + operators)
 *
 * Server is authoritative. Client validates upfront for fast UX feedback;
 * backend re-validates everything on the request handler.
 *
 * Schemas exported (request bodies):
 *   - createGroupRequestSchema   POST   /api/contacts/groups
 *   - updateGroupRequestSchema   PATCH  /api/contacts/groups/:id
 *   - bulkAddMembersRequestSchema POST  /api/contacts/groups/:id/members/bulk
 *   - filterDslSchema (composable)
 *
 * Types inferred via z.infer<typeof ...>.
 */

import { z } from "zod";

import { uuidSchema } from "./primitives";

// ─── Limits (mirror apps/api/src/lib/contacts-groups.ts GROUP_LIMITS) ────────

export const GROUP_LIMITS = {
  MAX_ACTIVE_GROUPS: 50,
  NAME_MAX: 60,
  COLOR_HEX_LEN: 6,
  MAX_BULK_MEMBERS: 1000,
  MEMBERS_PAGE_MAX: 200,
  MEMBERS_PAGE_DEFAULT: 50,
} as const;

// ─── Filter DSL limits (mirror apps/api/src/lib/contacts-filter.ts) ──────────

export const FILTER_LIMITS = {
  MAX_CONDITIONS: 10,
  MAX_VALUE_LEN: 200,
  MAX_DSL_BYTES: 4096,
} as const;

// ─── Group type enum ─────────────────────────────────────────────────────────

export const GROUP_TYPES = ["manual", "auto_filter", "import_batch"] as const;
export const groupTypeSchema = z.enum(GROUP_TYPES);
export type GroupType = z.infer<typeof groupTypeSchema>;

/**
 * Type creatable by user via POST /api/contacts/groups. import_batch is
 * auto-created by the import flow (S3) and rejected at this endpoint.
 */
export const creatableGroupTypeSchema = z.enum(["manual", "auto_filter"]);
export type CreatableGroupType = z.infer<typeof creatableGroupTypeSchema>;

// ─── Building blocks ─────────────────────────────────────────────────────────

const groupNameSchema = z
  .string()
  .trim()
  .min(1, "name_required")
  .max(GROUP_LIMITS.NAME_MAX, "name_too_long");

/**
 * 6-char hex color, no leading "#", lowercase. Examples: "6b7280", "0a84ff".
 * `null` is allowed at API level for "clear color" semantics on PATCH.
 */
const colorHexSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[0-9a-f]{6}$/, "color_must_be_6_hex_chars");

const optionalNullableColor = colorHexSchema
  .nullable()
  .optional();

// ─── Filter DSL ──────────────────────────────────────────────────────────────

/**
 * Filter DSL whitelist — mirrors apps/api/src/lib/contacts-filter.ts.
 *
 * Native fields use kebab-case-ish bare strings; custom fields use the
 * "custom:<key>" prefix where <key> is a user-defined contact field key.
 *
 * Each (field, op) pair has an enforced value type:
 *   has_email/has_phone : eq, value boolean
 *   created_at          : gte/lte, value number (unix seconds)
 *   source_id           : eq/neq, value uuid
 *   source_type         : eq/neq, value short string
 *   email_domain        : eq/neq, value lowercase domain
 *   custom:<key>        : eq/neq with value, OR exists/not_exists no value
 *
 * NOTE: Caller (UI) should never construct invalid combinations; server
 * also validates with the SAME rules and rejects mismatches with a
 * structured filter_errors array.
 */

const filterValueSchema = z
  .string()
  .max(FILTER_LIMITS.MAX_VALUE_LEN, "filter_value_too_long");

// Discriminated union per field — gives surgical typing in the UI builder
const conditionHasEmailSchema = z.object({
  field: z.literal("has_email"),
  op: z.literal("eq"),
  value: z.boolean(),
});

const conditionHasPhoneSchema = z.object({
  field: z.literal("has_phone"),
  op: z.literal("eq"),
  value: z.boolean(),
});

const conditionCreatedAtSchema = z.object({
  field: z.literal("created_at"),
  op: z.enum(["gte", "lte"]),
  value: z.number().int().nonnegative(),
});

const conditionSourceIdSchema = z.object({
  field: z.literal("source_id"),
  op: z.enum(["eq", "neq"]),
  value: uuidSchema,
});

const conditionSourceTypeSchema = z.object({
  field: z.literal("source_type"),
  op: z.enum(["eq", "neq"]),
  value: filterValueSchema,
});

const conditionEmailDomainSchema = z.object({
  field: z.literal("email_domain"),
  op: z.enum(["eq", "neq"]),
  value: z
    .string()
    .trim()
    .toLowerCase()
    .max(FILTER_LIMITS.MAX_VALUE_LEN, "filter_value_too_long")
    .regex(/^[a-z0-9.-]+\.[a-z]{2,}$/, "email_domain_invalid"),
});

const conditionCustomEqSchema = z.object({
  field: z.string().regex(/^custom:[a-z][a-z0-9_]{0,29}$/, "custom_key_invalid"),
  op: z.enum(["eq", "neq"]),
  value: filterValueSchema,
});

const conditionCustomExistsSchema = z.object({
  field: z.string().regex(/^custom:[a-z][a-z0-9_]{0,29}$/, "custom_key_invalid"),
  op: z.enum(["exists", "not_exists"]),
  // Note: no value — server tolerates value being absent OR null.
});

export const filterConditionSchema = z.union([
  conditionHasEmailSchema,
  conditionHasPhoneSchema,
  conditionCreatedAtSchema,
  conditionSourceIdSchema,
  conditionSourceTypeSchema,
  conditionEmailDomainSchema,
  conditionCustomEqSchema,
  conditionCustomExistsSchema,
]);

export type FilterCondition = z.infer<typeof filterConditionSchema>;

export const filterDslSchema = z.object({
  v: z.literal(1),
  conditions: z
    .array(filterConditionSchema)
    .min(1, "filter_must_have_conditions")
    .max(FILTER_LIMITS.MAX_CONDITIONS, "filter_too_many_conditions"),
});

export type FilterDSL = z.infer<typeof filterDslSchema>;

// ─── Request schemas ─────────────────────────────────────────────────────────

/**
 * POST /api/contacts/groups body.
 *
 * Server-side rules enforced by the route handler (NOT this schema):
 *   - import_batch type rejected (auto-created by import flow only)
 *   - filter_dsl required iff type === "auto_filter"
 *   - filter_dsl rejected iff type === "manual"
 *   - active group count check (MAX_ACTIVE_GROUPS)
 *
 * We accept type=manual|auto_filter here; backend further validates.
 */
export const createGroupRequestSchema = z
  .object({
    name: groupNameSchema,
    color: optionalNullableColor,
    type: creatableGroupTypeSchema,
    filter_dsl: filterDslSchema.optional(),
  })
  .strict();

export type CreateGroupRequest = z.infer<typeof createGroupRequestSchema>;

/**
 * PATCH /api/contacts/groups/:id body.
 *
 * At least one field required. Type is IMMUTABLE (rejected by .strict()).
 * archived_at is also NOT mutable here — separate DELETE/restore endpoints.
 */
export const updateGroupRequestSchema = z
  .object({
    name: groupNameSchema.optional(),
    color: optionalNullableColor,
    filter_dsl: filterDslSchema.optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.name !== undefined ||
      v.color !== undefined ||
      v.filter_dsl !== undefined,
    { message: "at_least_one_field_required" },
  );

export type UpdateGroupRequest = z.infer<typeof updateGroupRequestSchema>;

/**
 * POST /api/contacts/groups/:id/members/bulk body.
 *
 * IDs must all be UUIDs and all must belong to the user (server-side
 * IDOR check). Server hides which specific IDs are missing in error
 * responses (only `missing_count` exposed).
 */
export const bulkAddMembersRequestSchema = z
  .object({
    contact_ids: z
      .array(uuidSchema)
      .min(1, "contact_ids_required")
      .max(GROUP_LIMITS.MAX_BULK_MEMBERS, "too_many_contact_ids"),
  })
  .strict();

export type BulkAddMembersRequest = z.infer<typeof bulkAddMembersRequestSchema>;